import { useTranslation } from 'react-i18next';
import { openPortal } from '../../services/portal';
import type { UsageMode, UsageSummary } from '../../types/agent';

/** An amount in the usage's currency (EUR since PLAN-offers; USD from an older server). */
export function money(v: number, currency = 'USD', locale = 'en'): string {
  return new Intl.NumberFormat(locale === 'fr' ? 'fr-FR' : 'en-US', { style: 'currency', currency, minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v);
}

/** The usage line's tooltip: offer, period end, credits, the member's share. */
export function usageTitle(usage: UsageSummary, t: (k: string, o?: Record<string, unknown>) => string, locale: string): string {
  const c = usage.currency ?? 'USD';
  const parts = [t('composer.spendTitle')];
  if (usage.offer) parts.push(usage.period === 'trial' ? t('composer.trialOffer', { offer: usage.offer }) : usage.offer);
  if (usage.period_end) parts.push(t(usage.period === 'trial' ? 'composer.trialEnds' : 'composer.renews', { date: new Date(usage.period_end).toLocaleDateString(locale === 'fr' ? 'fr-FR' : 'en-GB') }));
  if (usage.scope === 'member') return parts.join(' · ');      // a member sees nothing of the organization's numbers
  if (usage.credits) parts.push(t('composer.credits', { amount: money(usage.credits, c, locale) }));
  if (usage.user_cap != null) parts.push(t('composer.yourShare', { spent: money(usage.user_spent ?? 0, c, locale), cap: money(usage.user_cap, c, locale) }));
  if (ownUsage(usage) !== null && usageMode(usage) === 'pool') parts.push(t('composer.orgPool', { spent: money(usage.spent_usd, c, locale), budget: money(usage.budget_usd, c, locale) }));
  return parts.join(' · ');
}

const NO_SUBSCRIPTION_REASONS = ['no_subscription', 'free_full', 'trial_ended'];

/** What the organization runs on: the server's `mode`, or for an older server derived from its other fields (it
 *  sent `unlimited` without a subscription, and a 0 pool with packs only). */
export function usageMode(usage: UsageSummary): UsageMode {
  if (usage.mode) return usage.mode;
  const noSubscription = usage.subscription_status == null;
  if (noSubscription && usage.limit_reached && NO_SUBSCRIPTION_REASONS.includes(usage.reason ?? '')) return 'none';
  if (noSubscription && !usage.unlimited && usage.budget_usd <= 0 && (usage.credits ?? 0) > 0) return 'credits';
  return usage.unlimited ? 'unlimited' : 'pool';
}

/** The viewer's own usage and limit, shown instead of the organization's pool (null: show the pool).
 *  - A member (`scope` "member"): always; the server already sends their own spend and limit (`budget_usd`, 0 = none)
 *    and nothing of the organization's.
 *  - An owner or admin with a limit of their own (`member_cap`: the organization's default or theirs): their spend
 *    against it, unless the organization's pool itself is spent (then the pool is what stops everyone).
 *  Never without a subscription (mode `none`: the limit message). */
export function ownUsage(usage: UsageSummary): { spent: number; cap: number | null } | null {
  if (usageMode(usage) === 'none') return null;
  if (usage.scope === 'member') return { spent: usage.spent_usd, cap: usage.budget_usd > 0 ? usage.budget_usd : null };
  if (usage.member_cap == null || usage.reason === 'limit_reached') return null;
  return { spent: usage.user_spent ?? 0, cap: usage.member_cap };
}

/** Share of the period's pool used, 0..1; 0 outside mode `pool` or without a pool. */
export function poolRatio(usage: UsageSummary): number {
  if (usageMode(usage) !== 'pool' || usage.budget_usd <= 0) return 0;
  return Math.min(1, Math.max(0, usage.spent_usd / usage.budget_usd));
}

/** Share of what the bar measures, 0..1: the viewer's own limit when shown (0 without one), else the pool. */
export function usageRatio(usage: UsageSummary): number {
  const own = ownUsage(usage);
  if (own === null) return poolRatio(usage);
  if (own.cap === null) return 0;
  if (own.cap <= 0) return 1;
  return Math.min(1, Math.max(0, own.spent / own.cap));
}

/** The bar's drawn width in %: the ratio, but at least a sliver once anything is spent. */
export function usageFill(usage: UsageSummary): number {
  const pct = Math.round(usageRatio(usage) * 100);
  const spent = ownUsage(usage)?.spent ?? usage.spent_usd;
  return spent > 0 ? Math.max(pct, 2) : pct;
}

const LINK = 'shrink-0 font-mono text-[11px] text-accent-dark underline hover:text-ink';

/** Under the composer before the usage ever loaded: a neutral line, so the composer keeps its height. */
export function UsagePlaceholder() {
  const { t } = useTranslation();
  return (
    <div className="flex min-w-0 items-center gap-2 px-0.5" data-testid="usage-placeholder">
      <span className="truncate font-mono text-[11px] text-faint">{t('composer.usageLoading')}</span>
    </div>
  );
}

/** Under the composer: the offer, the period's pool used as a thin bar, and the way to more. The line and the bar
 *  open the portal's usage page, signed in; at the limit everyone gets "Buy credits or change offer" (billing);
 *  admins on Free, a trial or past 80 % get "Upgrade". Amber from 80 %, red at the limit. Without a subscription:
 *  the credits left (mode `credits`), or the limit message and "Choose an offer" (mode `none`). A member sees only
 *  their own spend and limit ("Usage"); an owner or admin with a limit of their own sees it too, the pool in the
 *  tooltip (`ownUsage`). */
export function UsageBar({ usage, admin }: { usage: UsageSummary; admin: boolean }) {
  const { t, i18n } = useTranslation();
  const locale = i18n?.language ?? 'en';
  const c = usage.currency ?? 'USD';
  const mode = usageMode(usage);
  if (mode === 'none') {
    const message = t(`composer.limit.${usage.reason ?? 'no_subscription'}`, { cap: money(0, c, locale) });
    return (
      <div className="flex min-w-0 items-center gap-2 px-0.5" data-testid="usage-bar">
        <span className="min-w-0 flex-1 truncate font-mono text-[11px] text-danger" title={message} data-testid="composer-spend">{message}</span>
        <button type="button" onClick={() => void openPortal('/billing')} className={LINK} data-testid="composer-choose-offer">
          {t('composer.chooseOffer')} ↗
        </button>
      </div>
    );
  }
  const ratio = usageRatio(usage);
  const own = ownUsage(usage);
  const amount = own !== null
    ? own.cap !== null ? `${money(own.spent, c, locale)} / ${money(own.cap, c, locale)}` : money(own.spent, c, locale)
    : mode === 'credits'
    ? t('composer.creditsLeft', { amount: money(usage.credits ?? 0, c, locale) })
    : mode === 'unlimited' ? money(usage.spent_usd, c, locale) : `${money(usage.spent_usd, c, locale)} / ${money(usage.budget_usd, c, locale)}`;
  // What the amount measures: "Usage" for the member's own limit, "Free tier" on Free, else the offer (its pool).
  const what = own !== null ? t('composer.usage')
    : usage.free ? t('composer.freeTier')
    : usage.offer ? (usage.period === 'trial' ? t('composer.trialOffer', { offer: usage.offer }) : usage.offer) : null;
  const label = what ? `${what} · ${amount}` : amount;
  const tone = usage.limit_reached ? 'bg-danger' : ratio >= 0.8 ? 'bg-warn' : 'bg-accent';
  const atLimit = usage.limit_reached && !!usage.reason && usage.reason !== 'member_cap_reached';
  // A larger offer lifts the pool, not a member's own limit: "Upgrade" follows the pool.
  const upgrade = !atLimit && admin && (!!usage.free || usage.period === 'trial' || poolRatio(usage) >= 0.8);
  return (
    <div className="flex min-w-0 items-center gap-2 px-0.5" data-testid="usage-bar">
      <button
        type="button"
        onClick={() => void openPortal('/usage')}
        title={usageTitle(usage, t, locale)}
        className="flex min-w-0 flex-1 items-center gap-2 text-left"
        data-testid="composer-spend"
      >
        <span className={`shrink-0 truncate font-mono text-[11px] ${usage.limit_reached ? 'text-danger' : 'text-faint'}`}>{label}</span>
        {(own !== null ? own.cap !== null : mode === 'pool') && (
          <span className="h-1 min-w-[40px] flex-1 overflow-hidden rounded-full bg-hover" role="progressbar" aria-label={t('composer.spendTitle')} aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(ratio * 100)}>
            <span className={`block h-full rounded-full ${tone}`} style={{ width: `${usageFill(usage)}%` }} />
          </span>
        )}
      </button>
      {atLimit && (
        <button type="button" onClick={() => void openPortal('/billing')} className={LINK} data-testid="composer-billing">
          {t('composer.openBilling')} ↗
        </button>
      )}
      {upgrade && (
        <button type="button" onClick={() => void openPortal('/billing')} className={LINK} data-testid="composer-upgrade">
          {t('composer.upgrade')} ↗
        </button>
      )}
    </div>
  );
}
